// Компонент для отображения списка товаров с Wildberries
import { useState, useMemo } from 'react'
import { WBProduct, ComplianceCheck } from '../../types/wildberries'
import { RiskBadge, ComplianceScoreBadge } from './RiskBadge'
import { Search, Filter, ChevronUp, ChevronDown, CheckSquare, Square } from 'lucide-react'

interface ProductListProps {
  products: WBProduct[]
  checks?: Record<string, ComplianceCheck>
  onSelect: (ids: string[]) => void
  onCheck: (nm_id: string) => void
  onViewReport?: (nm_id: string) => void
  loading?: boolean
  checkLoading?: boolean
  showFilters?: boolean
}

type SortField = 'name' | 'category' | 'risk' | 'date'
type SortDirection = 'asc' | 'desc'

export const ProductList: React.FC<ProductListProps> = ({
  products,
  checks = {},
  onSelect,
  onCheck,
  onViewReport,
  loading = false,
  checkLoading = false,
  showFilters = true
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [filterRisk, setFilterRisk] = useState<'all' | 'high' | 'medium' | 'low' | 'none' | 'pending'>('all')
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // Обработка выбора товаров
  const handleSelect = (nm_id: string) => {
    const newSelected = selectedIds.includes(nm_id)
      ? selectedIds.filter(id => id !== nm_id)
      : [...selectedIds, nm_id]
    setSelectedIds(newSelected)
    onSelect(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedIds.length === filteredProducts.length) {
      setSelectedIds([])
      onSelect([])
    } else {
      const allIds = filteredProducts.map(p => p.nm_id)
      setSelectedIds(allIds)
      onSelect(allIds)
    }
  }

  // Фильтрация и сортировка
  const filteredProducts = useMemo(() => {
    let filtered = products.filter(product => {
      // Поиск по названию и артикулу
      const matchesSearch = searchQuery === '' ||
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.nm_id.includes(searchQuery)

      // Фильтр по рискам
      const check = checks[product.nm_id]
      if (filterRisk === 'all') return matchesSearch
      if (!check) return filterRisk === 'pending' && matchesSearch

      const score = check.compliance_score.value
      let riskLevel: 'high' | 'medium' | 'low' | 'none' | 'pending' = 'pending'
      if (score >= 0.9) riskLevel = 'none'
      else if (score >= 0.7) riskLevel = 'low'
      else if (score >= 0.5) riskLevel = 'medium'
      else riskLevel = 'high'

      return riskLevel === filterRisk && matchesSearch
    })

    // Сортировка
    filtered.sort((a, b) => {
      let aValue: any, bValue: any

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase()
          bValue = b.name.toLowerCase()
          break
        case 'category':
          aValue = a.category.toLowerCase()
          bValue = b.category.toLowerCase()
          break
        case 'risk': {
          const aCheck = checks[a.nm_id]
          const bCheck = checks[b.nm_id]
          aValue = aCheck ? aCheck.compliance_score.value : 1
          bValue = bCheck ? bCheck.compliance_score.value : 1
          break
        }
        case 'date':
          aValue = a.updated_at || a.created_at || ''
          bValue = b.updated_at || b.created_at || ''
          break
        default:
          return 0
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return filtered
  }, [products, checks, searchQuery, filterRisk, sortField, sortDirection])

  // Получение статуса проверки для товара
  const getProductStatus = (nm_id: string) => {
    const check = checks[nm_id]
    if (!check) return { level: 'pending', score: null }

    const score = check.compliance_score.value
    return {
      level: score >= 0.9 ? 'none' : score >= 0.7 ? 'low' : score >= 0.5 ? 'medium' : 'high',
      score
    }
  }

  // Обработчик сортировки
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Скелетон загрузки
  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="bg-gray-100 animate-pulse h-16 rounded-lg"></div>
        ))}
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Панель управления */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Поиск по названию или артикулу..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {showFilters && (
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Filter size={18} className="text-gray-500" />
                <select
                  value={filterRisk}
                  onChange={(e) => setFilterRisk(e.target.value as any)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">Все риски</option>
                  <option value="pending">Не проверено</option>
                  <option value="none">Нет рисков</option>
                  <option value="low">Низкий риск</option>
                  <option value="medium">Средний риск</option>
                  <option value="high">Высокий риск</option>
                </select>
              </div>

              <button
                onClick={() => {
                  if (selectedIds.length > 0) {
                    selectedIds.forEach(id => onCheck(id))
                  }
                }}
                disabled={selectedIds.length === 0 || checkLoading}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Проверить выбранные ({selectedIds.length})
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Таблица товаров */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3 px-4 text-left">
                <button
                  onClick={handleSelectAll}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700"
                >
                  {selectedIds.length === filteredProducts.length && filteredProducts.length > 0 ? (
                    <CheckSquare size={18} className="text-indigo-600" />
                  ) : (
                    <Square size={18} className="text-gray-400" />
                  )}
                  Выбрать все
                </button>
              </th>
              <th className="py-3 px-4 text-left">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-1 text-sm font-medium text-gray-700"
                >
                  Название
                  {sortField === 'name' && (
                    sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                  )}
                </button>
              </th>
              <th className="py-3 px-4 text-left">
                <button
                  onClick={() => handleSort('category')}
                  className="flex items-center gap-1 text-sm font-medium text-gray-700"
                >
                  Категория
                  {sortField === 'category' && (
                    sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                  )}
                </button>
              </th>
              <th className="py-3 px-4 text-left">
                <button
                  onClick={() => handleSort('risk')}
                  className="flex items-center gap-1 text-sm font-medium text-gray-700"
                >
                  Статус проверки
                  {sortField === 'risk' && (
                    sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                  )}
                </button>
              </th>
              <th className="py-3 px-4 text-left text-sm font-medium text-gray-700">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredProducts.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-8 text-center text-gray-500">
                  {products.length === 0 ? 'Товары не загружены' : 'Товары не найдены'}
                </td>
              </tr>
            ) : (
              filteredProducts.map(product => {
                const status = getProductStatus(product.nm_id)
                const check = checks[product.nm_id]

                return (
                  <tr key={product.nm_id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(product.nm_id)}
                        onChange={() => handleSelect(product.nm_id)}
                        className="h-4 w-4 text-indigo-600 rounded focus:ring-indigo-500"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-gray-900">{product.name}</p>
                        <p className="text-sm text-gray-500">Артикул: {product.nm_id}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-block px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                        {product.category}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {status.score !== null ? (
                        <div className="flex items-center gap-2">
                          <ComplianceScoreBadge score={status.score} size="sm" />
                          {check && check.has_risks && (
                            <span className="text-sm text-gray-600">
                              {check.high_severity_risks.length} высоких рисков
                            </span>
                          )}
                        </div>
                      ) : (
                        <RiskBadge level="pending" size="sm" />
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onCheck(product.nm_id)}
                          disabled={checkLoading}
                          className="px-3 py-1.5 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-lg hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Проверить
                        </button>
                        {check && (
                          <button
                            onClick={() => {
                              console.log('📋 Нажатие "Отчёт" для', product.nm_id)
                              if (onViewReport) {
                                onViewReport(product.nm_id)
                              }
                            }}
                            className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                          >
                            Отчёт
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Пагинация/статус */}
      <div className="p-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="text-sm text-gray-600">
          Показано {filteredProducts.length} из {products.length} товаров
          {selectedIds.length > 0 && ` • Выбрано: ${selectedIds.length}`}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <RiskBadge level="pending" size="sm" showIcon={false} />
            <span className="text-sm text-gray-600">Не проверено</span>
          </div>
          <div className="flex items-center gap-2">
            <RiskBadge level="none" size="sm" showIcon={false} />
            <span className="text-sm text-gray-600">Нет рисков</span>
          </div>
          <div className="flex items-center gap-2">
            <RiskBadge level="high" size="sm" showIcon={false} />
            <span className="text-sm text-gray-600">Высокий риск</span>
          </div>
        </div>
      </div>
    </div>
  )
}